<template>
  <div :class="classes.listWrapperContainer">
    <div :class="[classes.fadeTop, { [classes.showFade]: !isTop }]" />
    <div ref="target" :class="[classes.listWrapper, layoutClass]">
      <slot></slot>
    </div>
    <div :class="[{ [classes.showFade]: !isBottom }, classes.fade]" />
  </div>
</template>

<script lang="ts">
import {
  BrandContext,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import { injectTemplateSize } from "@fresh8/ad-data-composition/src/useTemplateSize";
import { createUseStyles } from "@fresh8/vue-jss/src";
import { computed, onMounted, ref, defineComponent } from "vue";

const useStyles = createUseStyles({
  listWrapperContainer: {
    display: "flex",
    position: "relative",
    minHeight: 0,
    flex: "1 1 1px",
    overflow: "hidden"
  },
  listWrapper: {
    width: "100%",
    overflowY: "scroll",
    overflowX: "hidden",
    boxSizing: "border-box",
    position: "relative",
    display: "grid",
    gridColumnGap: props => `${props.gap}px`,
    gridRowGap: props => `${props.gap}px`,
    rowGap: props => `${props.gap}px`,
    columnGap: props => `${props.gap}px`,
    gridTemplateColumns: props => {
      let value = props.colWidth === 0 ? "1fr" : `${props.colWidth}px`;
      return `repeat(auto-fit, minmax(${value}, 1fr))`;
    },
    gridTemplateRows: props =>
      props.elementHeight ? `${props.elementHeight}px` : "1fr",
    gridAutoRows: props =>
      props.elementHeight ? `${props.elementHeight}px` : "1fr",
    maxHeight: props => props.containerHeight,
    padding: props => `${props.pt || 0}px ${props.px || 0}px ${props.pb || 0}px`
  },
  fade: {
    pointerEvents: "none",
    position: "absolute",
    height: "15%",
    maxHeight: "30px",
    bottom: "-2px",
    left: 0,
    right: 0,
    opacity: 0,
    borderRadius: props => `0 0 ${props.br}px ${props.br}px`,
    transition: "opacity 0.25s",
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(0,0,0,.35) 100%)`
  },
  showFade: {
    opacity: "1 !important"
  },
  fadeTop: {
    pointerEvents: "none",
    position: "absolute",
    height: "15%",
    maxHeight: "30px",
    zIndex: 1,
    top: 0,
    left: 0,
    right: 0,
    opacity: 0,

    transition: "opacity 0.25s",
    background: `linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(0,0,0,.35) 100%)`
  },
  spacer: {
    display: "flex",
    gridColumn: "1 / -1"
  }
});

export default defineComponent({
  name: "ScollableContainer",
  props: {
    elementHeight: {
      type: Number,
      required: false,
      default: undefined
    },
    containerHeight: {
      type: Number,
      required: false,
      default: undefined
    },
    colWidth: {
      type: Number,
      required: false,
      default: 200
    },
    pt: {
      type: Number,
      required: false,
      default: 0
    },
    pb: {
      type: Number,
      required: false,
      default: 0
    },
    px: {
      type: Number,
      required: false,
      default: 8
    },
    gap: {
      type: Number,
      required: false,
      default: 8
    },
    shadowRadius: {
      type: String,
      required: false,
      default: "round" as const
    }
  },
  emits: ["scrolledTop", "scrolledBottom", "scroll"],
  setup(props, ctx) {
    const isTop = ref(true);
    const isBottom = ref(true);
    const target = ref<Element | null>(null);
    const brand = injectBrand() as BrandContext;

    onMounted(() => {
      if (target.value) {
        const { scrollHeight, clientHeight, scrollTop } = target.value;

        // need to check if there are enough items in list to make container on mount and show the bottom fade or not
        isBottom.value =
          Math.round(scrollHeight) - Math.round(scrollTop) <
          Math.round(clientHeight) + (props.pb || 0);

        target.value.addEventListener("scroll", e => {
          const {
            scrollHeight,
            scrollTop,
            clientHeight
          } = e.target! as HTMLElement;

          // is it scrolled all the way to the top
          isTop.value = Math.floor(scrollTop) === 0;

          if (isTop.value) ctx.emit("scrolledTop");

          //is it scrolled all the way to the bottom (same calculation as above)
          isBottom.value =
            Math.round(scrollHeight) - Math.round(scrollTop) <
            Math.round(clientHeight) + (props.pb || 0);

          if (isBottom.value) ctx.emit("scrolledBottom");

          ctx.emit("scroll", e.target!);
        });
      }
    });

    const reactiveStyles = computed(() => ({
      elementHeight: props.elementHeight,
      containerHeight: props.containerHeight,
      pb: props.pb,
      pt: props.pt,
      gap: props.gap,
      px: props.px,
      colWidth: props.colWidth,
      br:
        props.shadowRadius !== "round"
          ? 0
          : brand?.adTemplate?.value?.cardBorderRadius
    }));

    const classes = useStyles(reactiveStyles);

    const templateSizeContext = injectTemplateSize();

    const layoutClass = computed(() => templateSizeContext?.size.value.name);

    return { layoutClass, target, isTop, isBottom, classes };
  }
});
</script>
